import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useDashboard } from "../context/DashboardContext";
import { api } from "../api";

// Date presets
function getPresets() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed
  const today = now.toISOString().slice(0, 10);

  const monthStr = (yr: number, mo: number) => `${yr}-${String(mo + 1).padStart(2, "0")}`;
  const dateStr = (yr: number, mo: number, d: number) => `${yr}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const firstOfMonth = (yr: number, mo: number) => dateStr(yr, mo, 1);
  const lastOfMonth = (yr: number, mo: number) => {
    const last = new Date(yr, mo + 1, 0);
    return dateStr(last.getFullYear(), last.getMonth(), last.getDate());
  };
  const daysAgo = (n: number) => {
    const d = new Date(now.getTime() - n * 86400000);
    return d.toISOString().slice(0, 10);
  };

  // Previous month
  const pm = m === 0 ? 11 : m - 1;
  const py = m === 0 ? y - 1 : y;

  return [
    { label: "This Month", mode: "month" as const, month: monthStr(y, m) },
    { label: "Last Month", mode: "month" as const, month: monthStr(py, pm) },
    { label: "Last 7 Days", mode: "range" as const, start: daysAgo(7), end: today },
    { label: "Last 14 Days", mode: "range" as const, start: daysAgo(14), end: today },
    { label: "Last 30 Days", mode: "range" as const, start: daysAgo(30), end: today },
    { label: "This Quarter", mode: "range" as const, start: firstOfMonth(y, Math.floor(m / 3) * 3), end: today },
    { label: "Last 3 Months", mode: "range" as const, start: firstOfMonth(m >= 3 ? y : y - 1, m >= 3 ? m - 3 : m + 9), end: lastOfMonth(py, pm) },
  ];
}

export default function Topbar() {
  const { user, logout } = useAuth();
  const { customer, setCustomer, dateMode, setDateMode, month, setMonth, startDate, setStartDate, endDate, setEndDate, dateLabel } = useDashboard();
  const [customers, setCustomers] = useState<Record<string, { key: string; name: string }>>({});
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    api<Record<string, { key: string; name: string }>>("/api/customers").then((data) => {
      setCustomers(data);
      const keys = Object.keys(data);
      if (keys.length > 0 && !customer) setCustomer(keys[0]);
    });
  }, []);

  const presets = getPresets();

  const applyPreset = (p: typeof presets[0]) => {
    if (p.mode === "month") {
      setDateMode("month");
      setMonth(p.month!);
    } else {
      setDateMode("range");
      setStartDate(p.start!);
      setEndDate(p.end!);
    }
    setShowDatePicker(false);
  };

  return (
    <nav className="flex items-center justify-between px-6 py-3 bg-[#1a1d27] border-b border-[#2a2d3e] sticky top-0 z-50 backdrop-blur-xl">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">V</div>
        <span className="text-lg font-semibold">Dakok <span className="text-blue-400">Vista</span></span>
      </div>

      <div className="flex items-center gap-3">
        {/* Customer selector */}
        <select value={customer} onChange={(e) => setCustomer(e.target.value)}
          className="px-3 py-2 rounded-lg border border-[#2a2d3e] bg-[#1e2130] text-sm outline-none focus:border-blue-500 cursor-pointer">
          {Object.entries(customers).map(([key, c]) => (
            <option key={key} value={key}>{c.name}</option>
          ))}
        </select>

        {/* Date picker button */}
        <div className="relative">
          <button onClick={() => setShowDatePicker(!showDatePicker)}
            className="px-3 py-2 rounded-lg border border-[#2a2d3e] bg-[#1e2130] text-sm outline-none hover:border-blue-500 transition-colors flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{dateLabel}</span>
            <span className="text-[10px] text-gray-500 uppercase">{dateMode === "month" ? "month" : "range"}</span>
          </button>

          {/* Dropdown */}
          {showDatePicker && (
            <div className="absolute top-full right-0 mt-2 w-[340px] bg-[#1e2130] border border-[#2a2d3e] rounded-xl shadow-2xl shadow-black/40 z-50 overflow-hidden">
              {/* Presets */}
              <div className="p-3 border-b border-[#2a2d3e]">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Quick Select</div>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((p) => {
                    const isActive = (p.mode === "month" && dateMode === "month" && p.month === month)
                      || (p.mode === "range" && dateMode === "range" && p.start === startDate && p.end === endDate);
                    return (
                      <button key={p.label} onClick={() => applyPreset(p)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs transition-all ${isActive
                          ? "bg-blue-500 text-white"
                          : "bg-[#0f1117] text-gray-400 hover:text-white hover:bg-[#252840] border border-[#2a2d3e]"}`}>
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Month picker */}
              <div className="p-3 border-b border-[#2a2d3e]">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">By Month</div>
                <input type="month" value={dateMode === "month" ? month : ""}
                  onChange={(e) => { setDateMode("month"); setMonth(e.target.value); setShowDatePicker(false); }}
                  className="w-full px-3 py-2 rounded-lg border border-[#2a2d3e] bg-[#0f1117] text-sm outline-none focus:border-blue-500" />
              </div>

              {/* Custom date range */}
              <div className="p-3">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Custom Date Range</div>
                <div className="flex gap-2 items-center">
                  <input type="date" value={dateMode === "range" ? startDate : ""}
                    onChange={(e) => { setDateMode("range"); setStartDate(e.target.value); }}
                    className="flex-1 px-2 py-2 rounded-lg border border-[#2a2d3e] bg-[#0f1117] text-sm outline-none focus:border-blue-500" />
                  <span className="text-gray-500 text-xs">→</span>
                  <input type="date" value={dateMode === "range" ? endDate : ""}
                    onChange={(e) => { setDateMode("range"); setEndDate(e.target.value); }}
                    className="flex-1 px-2 py-2 rounded-lg border border-[#2a2d3e] bg-[#0f1117] text-sm outline-none focus:border-blue-500" />
                </div>
                <button onClick={() => setShowDatePicker(false)}
                  className="w-full mt-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
                  Apply Range
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-400">{user?.email}</span>
        <button onClick={logout}
          className="px-3 py-1.5 rounded-lg border border-[#2a2d3e] text-gray-400 text-sm hover:border-blue-500 hover:text-blue-400 transition-all">
          Sign Out
        </button>
      </div>
    </nav>
  );
}
